import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import Login from './Login';
import Register from './Register';
import Dashboard from './Dashboard';
import PublicLanding from './PublicLanding';

/** A belépő oldal útvonala. [Web UX 2. csomag – Belépés és munkamenet, 2026-10-03] */
export const LOGIN_PATH = '/belepes';
/** A regisztrációs oldal útvonala. */
export const REGISTER_PATH = '/regisztracio';

/**
 * A `/` (és minden ismeretlen út): bejelentkezve a Dashboard, egyébként a nyilvános nyitóoldal.
 *
 * [Web UX 2. csomag – Belépés és munkamenet, 2026-10-03] KORÁBBAN a Belépés és a Regisztráció
 * ennek a komponensnek a BELSŐ állapota volt (`showLogin`/`showRegister`, a régi
 * `AuthWrapper.tsx:11-12`), útvonal nélkül — ezért a `ResetPassword`/`VerifyEmail`
 * `navigate('/login')`-ja a nyilvános nyitóoldalra esett, és a regisztráció közbeni F5 is oda dobott
 * (jelentés 3.1). Most a belépés `/belepes`, a regisztráció `/regisztracio` (lásd `App.tsx`).
 */
const AuthWrapper: React.FC = () => {
  const { user, loading, sessionEndCount = 0 } = useAuth();
  const navigate = useNavigate();
  // A munkamenet MINDEN végén (lejárat, másik fül, és [validátor B1, 2026-10-03] a KÉZI
  // kijelentkezés is) a belépő oldalra (`/belepes`) visszük a felhasználót — a régi felület is a
  // belépő űrlapot mutatta kilépés után. Az üzenetet a `sessionStorage`-jelző dönti el (kézi
  // kilépésnél nincs). A felcsatoláskori számlálót jegyezzük meg: ha valaki később (pl. a belépő
  // oldal „Vissza" gombjával) tér vissza a `/`-ra, ne dobjuk újra a belépésre.
  const [countAtMount] = useState(sessionEndCount);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (user) {
    return <Dashboard />;
  }

  if (sessionEndCount !== countAtMount) {
    return <Navigate to={LOGIN_PATH} replace />;
  }

  return (
    <PublicLanding
      onNavigateToLogin={() => navigate(LOGIN_PATH)}
      onNavigateToRegister={() => navigate(REGISTER_PATH)}
    />
  );
};

/** `/belepes` — bejelentkezett felhasználót a `/`-ra (Dashboard) visz; sikeres belépés után ugyanígy. */
export const LoginPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  if (user) {
    return <Navigate to="/" replace />;
  }
  return <Login onSwitchToRegister={() => navigate(REGISTER_PATH)} onBack={() => navigate('/')} />;
};

/**
 * `/regisztracio` — bejelentkezett felhasználót a `/`-ra visz. A sikeres regisztráció üzenete a
 * `Register` saját állapota (`isRegistered`), az útvonalon maradva látszik tovább. Az ŰRLAP ADATAI
 * F5 után továbbra is elvesznek: a jelszó miatt szándékosan nem perzisztálunk (sessionStorage-be sem).
 */
export const RegisterPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  if (user) {
    return <Navigate to="/" replace />;
  }
  return <Register onSwitchToLogin={() => navigate(LOGIN_PATH)} onBack={() => navigate('/')} />;
};

export default AuthWrapper;
